/**
 * Definit les donnees de la lecon Graphic Design.
 * La configuration vide fournit les valeurs du formulaire,
 * et la normalisation protege la lecture des donnees stockees.
 */
import { normalizeHex } from '../../components/GraphicDesignLesson/core/color';

export type TI18nText = { fr: string; en?: string; ar?: string };
export type TGDExerciseType = 'CREATE' | 'INTENT' | 'CRITIQUE' | 'CHOOSE' | 'TRANSFORM';
export type TGDTool = 'shape' | 'pictogram' | 'text' | 'palette' | 'layers' | 'gradient';
export type TGDTier = 'required' | 'constraint' | 'bonus';
export type TGDComponentType = 'shape' | 'pictogram' | 'text';

export interface TGDComponent {
  id: string;
  type: TGDComponentType;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  fill: string;
  stroke?: string;
  strokeWidth?: number;
  layer: number;
  role?: string;
  sides?: number;
  cornerRadius?: number;
  icon?: string;
  text?: string;
  fontSize?: number;
  fontPair?: string;
  fontWeight?: 400 | 600 | 800;
  align?: 'left' | 'center' | 'right';
  direction?: 'ltr' | 'rtl';
}

export interface TGDDesign { background: string; components: TGDComponent[] }
export type TGDSelector =
  | { by: 'id'; value: string }
  | { by: 'role'; value: string }
  | { by: 'type'; value: TGDComponentType }
  | { by: 'text'; value: string }
  | { by: 'icon'; value: string }
  | { by: 'background' };
export type TGDAxis = 'left' | 'centerX' | 'right' | 'top' | 'centerY' | 'bottom';
export type TGDHarmony = 'complementary' | 'analogous' | 'triadic';
export type TGDComparableProp =
  | 'x' | 'y' | 'width' | 'height' | 'rotation' | 'fontSize' | 'sides' | 'cornerRadius'
  | 'fill' | 'stroke' | 'text' | 'icon' | 'background';

export type TGDRuleSpec =
  | { type: 'requiredElement'; params: { select: TGDSelector; minCount?: number } }
  | { type: 'elementCountMax'; params: { value: number; componentType?: TGDComponentType } }
  | { type: 'maxColors'; params: { value: number; includeBackground?: boolean } }
  | { type: 'minColors'; params: { value: number; includeBackground?: boolean } }
  | { type: 'contrastMin'; params: { a: TGDSelector; b?: TGDSelector; value: number } }
  | { type: 'usesPalette'; params: { harmony: TGDHarmony; toleranceDeg?: number } }
  | { type: 'sizeRatioMin'; params: { a: TGDSelector; b: TGDSelector; value: number } }
  | { type: 'hierarchyOrder'; params: { order: TGDSelector[]; minRatio?: number } }
  | { type: 'alignedOn'; params: { select: TGDSelector[]; axis: TGDAxis; tolerance?: number } }
  | { type: 'timeMax'; params: { seconds: number } }
  | { type: 'zoneFound'; params: { zoneId: string } }
  | { type: 'choiceEquals'; params: { choiceId: string } }
  | { type: 'reasonEquals'; params: { reasonId: string } }
  | { type: 'changed'; params: { select: TGDSelector; prop: TGDComparableProp; minDelta?: number } }
  | { type: 'preserved'; params: { select: TGDSelector; prop: TGDComparableProp; tolerance?: number } };
export type TGDRuleType = TGDRuleSpec['type'];
export type TGDRule = TGDRuleSpec & {
  id: string;
  tier: TGDTier;
  label: TI18nText;
  failMessage?: TI18nText;
};

export interface TGDZone {
  id: string;
  x: number; y: number; w: number; h: number;
  options: TI18nText[];
  correct: number;
  lesson: TI18nText;
}
export interface TGDVersion { id: string; label?: TI18nText; design: TGDDesign }
export interface TGDChoice { id: string; label: TI18nText }
export type TGDInitialState =
  | { mode: 'inline'; design: TGDDesign }
  | { mode: 'fromLesson'; lessonId: number; fallback: TGDDesign };

export interface TGraphicDesignLessonConfig {
  statement?: string;
  exerciseType: TGDExerciseType;
  brief: TI18nText;
  clientName?: string;
  artboard: { width: number; height: number };
  background: string;
  allowedTools: TGDTool[];
  lockedPalette?: string[];
  maxElements?: number;
  initialState?: TGDInitialState;
  rules: TGDRule[];
  requiredRules: number;
  hints: TI18nText[];
  reward?: { unlockTool: TGDTool; atStars: 2 | 3 };
  backgroundImage?: string;
  zones?: TGDZone[];
  versions?: TGDVersion[];
  choices?: TGDChoice[];
  proofRules?: TGDRule[];
  solution?: TGDDesign;
}

export const GD_EXERCISE_TYPES: TGDExerciseType[] = [
  'CREATE', 'INTENT', 'CRITIQUE', 'CHOOSE', 'TRANSFORM',
];
export const GD_TOOLS: TGDTool[] = ['shape', 'pictogram', 'text', 'palette', 'layers', 'gradient'];
const isObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);
const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/** Fournit les valeurs initiales pour une nouvelle lecon. */
export const createEmptyGraphicDesignLessonConfig = (): TGraphicDesignLessonConfig => ({
  statement: '',
  exerciseType: 'CREATE',
  brief: { fr: '' },
  artboard: { width: 600, height: 800 },
  background: '#ffffff',
  allowedTools: ['shape', 'pictogram', 'text', 'palette'],
  rules: [],
  requiredRules: 0,
  hints: [],
});

/**
 * Conserve les champs connus avec leurs valeurs valides.
 * Les entrees des tableaux seront verifiees par le moteur.
 */
export const normalizeGraphicDesignLessonConfig = (raw: unknown): TGraphicDesignLessonConfig => {
  const result = createEmptyGraphicDesignLessonConfig();
  if (!isObject(raw)) return result;
  if (GD_EXERCISE_TYPES.includes(raw.exerciseType as TGDExerciseType)) {
    result.exerciseType = raw.exerciseType as TGDExerciseType;
  }
  if (isObject(raw.artboard) && isFiniteNumber(raw.artboard.width) && raw.artboard.width > 0 &&
      isFiniteNumber(raw.artboard.height) && raw.artboard.height > 0) {
    result.artboard = { width: raw.artboard.width, height: raw.artboard.height };
  }
  if (typeof raw.background === 'string') {
    result.background = normalizeHex(raw.background) ?? result.background;
  }
  if (Array.isArray(raw.allowedTools)) result.allowedTools = raw.allowedTools.filter(
    (tool): tool is TGDTool => GD_TOOLS.includes(tool)
  );
  if (Array.isArray(raw.rules)) result.rules = raw.rules as TGDRule[];
  if (Array.isArray(raw.hints)) result.hints = raw.hints as TI18nText[];
  if (Array.isArray(raw.zones)) result.zones = raw.zones as TGDZone[];
  if (Array.isArray(raw.versions)) result.versions = raw.versions as TGDVersion[];
  if (Array.isArray(raw.choices)) result.choices = raw.choices as TGDChoice[];
  if (Array.isArray(raw.proofRules)) result.proofRules = raw.proofRules as TGDRule[];
  if (isFiniteNumber(raw.requiredRules) && raw.requiredRules >= 0) {
    result.requiredRules = Math.trunc(raw.requiredRules);
  }
  if (typeof raw.statement === 'string') result.statement = raw.statement;
  if (typeof raw.clientName === 'string') result.clientName = raw.clientName;
  if (typeof raw.backgroundImage === 'string') result.backgroundImage = raw.backgroundImage;
  if (isFiniteNumber(raw.maxElements) && Number.isInteger(raw.maxElements) && raw.maxElements > 0) {
    result.maxElements = Math.trunc(raw.maxElements);
  }
  if (Array.isArray(raw.lockedPalette)) result.lockedPalette = raw.lockedPalette.filter(
    (color): color is string => typeof color === 'string'
  );
  if (isObject(raw.brief) && typeof raw.brief.fr === 'string') result.brief = raw.brief as TI18nText;
  if (isObject(raw.initialState)) result.initialState = raw.initialState as TGDInitialState;
  if (isObject(raw.reward)) result.reward = raw.reward as TGraphicDesignLessonConfig['reward'];
  if (isObject(raw.solution)) result.solution = raw.solution as unknown as TGDDesign;
  return result;
};
